import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FilePlus2, Landmark } from 'lucide-react';
import type { PaymentRequest, RequestStatus } from '@ach/shared';
import { formatUsd } from '@ach/shared';

import { api } from '../lib/api.js';
import { Spinner, ErrorNote, StatusBadge, EmptyState } from './components.js';

type Row = PaymentRequest & { customerName: string; customerEmail: string };

export function Dashboard() {
  const [requests, setRequests] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ requests: Row[] }>('/api/admin/payment-requests?limit=200')
      .then((data) => setRequests(data.requests))
      .catch(() => setError('Could not load payment requests.'));
  }, []);

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (requests === null) return <Spinner label="Loading dashboard" />;

  // Submitted but not yet recorded: the customer has done their part and is
  // waiting on us. It leads because it is the only bucket with an action in it.
  const awaiting = requests.filter((r) => r.status === 'submitted');
  const open = requests.filter((r) => r.status === 'sent' || r.status === 'viewed');
  const drafts = requests.filter((r) => r.status === 'draft');
  const done = requests.filter((r) => r.status === 'recorded');

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold text-slate-900">Dashboard</h1>
        <Link to="/admin/requests/new" className="btn-primary ml-auto">
          <FilePlus2 className="h-4 w-4" aria-hidden />
          New payment request
        </Link>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <Stat label="Awaiting recording" value={awaiting.length} accent />
        <Stat label="Out with customers" value={open.length} />
        <Stat label="Drafts" value={drafts.length} />
        <Stat label="Recorded" value={done.length} />
      </div>

      {awaiting.length > 0 && (
        <Section
          title="Ready to schedule at the bank"
          hint="These customers have submitted their bank details."
          icon={Landmark}
          rows={awaiting}
        />
      )}

      <Section title="Out with customers" rows={open} emptyText="Nothing waiting on a customer." />
      {drafts.length > 0 && <Section title="Drafts" rows={drafts} />}
      <Section title="Recorded" rows={done.slice(0, 20)} emptyText="Nothing recorded yet." />
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className={`card p-4 ${accent && value > 0 ? 'border-gold-300 bg-gold-50' : ''}`}>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
    </div>
  );
}

function Section({
  title,
  hint,
  rows,
  emptyText,
  icon: Icon,
}: {
  title: string;
  hint?: string;
  rows: Row[];
  emptyText?: string;
  icon?: typeof Landmark;
}) {
  return (
    <section className="space-y-3">
      <div>
        <h2 className="flex items-center gap-2 font-semibold text-slate-900">
          {Icon && <Icon className="h-4 w-4 text-gold-600" aria-hidden />}
          {title}
        </h2>
        {hint && <p className="text-sm text-slate-500">{hint}</p>}
      </div>

      {rows.length === 0 ? (
        emptyText ? (
          <EmptyState title={emptyText} />
        ) : null
      ) : (
        <div className="card divide-y divide-slate-100">
          {rows.map((request) => (
            <Link
              key={request.id}
              to={`/admin/requests/${request.id}`}
              className="flex items-center gap-4 px-4 py-3 text-sm hover:bg-slate-50"
            >
              <span className="w-28 shrink-0 text-slate-500">
                {new Date(request.createdAt).toLocaleDateString()}
              </span>
              <span className="min-w-0 flex-1 truncate font-medium text-slate-800">
                {request.customerName}
              </span>
              <span className="w-28 shrink-0 text-right font-medium text-slate-900">
                {formatUsd(request.totalAmountCents)}
              </span>
              <span className="w-24 shrink-0 text-right">
                <StatusBadge status={request.status as RequestStatus} />
              </span>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}
